import { type FC, memo } from 'react';
import { Layout } from 'antd';
import { useBreakpoint } from '../../hooks/use-media-query';

const { Footer } = Layout;

export interface AppFooterProps {
  className?: string;
  fixed?: boolean;
  fullWidth?: boolean;
  akvakimLogoSrc?: string;
  tidepoolLogoSrc?: string;
}

// Breakpoint-based footer configuration
const getFooterConfig = (breakpoint: 'mobile' | 'tablet' | 'desktop' | 'largeDesktop') => {
  switch (breakpoint) {
    case 'mobile':
      return {
        height: 'clamp(26px, 4.2vw, 32px)',
        padding: 'clamp(6px, 1.5vh, 12px) 0',
        containerWidth: 'clamp(200px, 95vw, 320px)',
        fontSize: 'clamp(13px, 4.2vw, 18px)',
        logoHeight: 'clamp(13px, 4.2vw, 18px)',
        gap: 'clamp(3.5px, 2vw, 10px)',
      };
    case 'tablet':
      return {
        height: '28px',
        padding: '14px 0',
        containerWidth: '800px',
        fontSize: '14px',
        logoHeight: '14px',
        gap: '14px',
      };
    case 'desktop':
      return {
        height: '32px',
        padding: '16px 0',
        containerWidth: '900px',
        fontSize: '16px',
        logoHeight: '16px',
        gap: '16px',
      };
    case 'largeDesktop':
      return {
        height: '38px',
        padding: '18px 0',
        containerWidth: '1000px',
        fontSize: '18px',
        logoHeight: '18px',
        gap: '18px',
      };
    default:
      return {
        height: '28px',
        padding: '14px 0',
        containerWidth: '800px',
        fontSize: '14px',
        logoHeight: '14px',
        gap: '14px',
      };
  }
};

/**
 * AppFooter - Shared Footer Component
 * Displays attribution text with company logos
 * "Designed for [AKVAKIM] Powered by [TIDEPOOL]"
 * Progressive sizing: mobile → tablet → desktop → largeDesktop
 */
export const AppFooter: FC<AppFooterProps> = memo(({
  className,
  fixed = true,
  fullWidth = false,
  akvakimLogoSrc,
  tidepoolLogoSrc,
}) => {
  const breakpoint = useBreakpoint();
  const config = getFooterConfig(breakpoint);

  return (
    <Footer
      className={className}
      style={{
        position: fixed ? 'fixed' : 'relative',
        bottom: fixed ? 0 : undefined,
        left: fixed ? 0 : undefined,
        right: fixed ? 0 : undefined,
        zIndex: fixed ? 1000 : undefined,
        background: 'rgba(44, 44, 46, 1)',
        padding: config.padding,
        height: config.height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: fullWidth ? '100vw' : (fixed ? undefined : '100%'),
        marginLeft: fullWidth ? 'calc(-50vw + 50%)' : undefined,
        marginRight: fullWidth ? 'calc(-50vw + 50%)' : undefined,
        maxWidth: fullWidth ? '100vw' : undefined,
      }}
    >
      <div
        style={{
          width: config.containerWidth,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: config.gap,
        }}
      >
        <span
          style={{
            fontFamily: '"Noto Sans", sans-serif',
            fontWeight: 400,
            fontSize: config.fontSize,
            lineHeight: '100%',
            letterSpacing: '0%',
            textAlign: 'center',
            color: '#FFFFFF',
            whiteSpace: 'nowrap',
          }}
        >
          Designed for
        </span>
        {akvakimLogoSrc && (
          <img
            src={akvakimLogoSrc}
            alt="AKVAKIM"
            style={{
              height: config.logoHeight,
              objectFit: 'contain',
              verticalAlign: 'middle',
              userSelect: 'none',
            }}
          />
        )}
        <span
          style={{
            fontFamily: '"Noto Sans", sans-serif',
            fontWeight: 400,
            fontSize: config.fontSize,
            lineHeight: '100%',
            letterSpacing: '0%',
            textAlign: 'center',
            color: '#FFFFFF',
            whiteSpace: 'nowrap',
          }}
        >
          Powered by
        </span>
        {tidepoolLogoSrc && (
          <img
            src={tidepoolLogoSrc}
            alt="TIDEPOOL"
            style={{
              height: config.logoHeight,
              objectFit: 'contain',
              verticalAlign: 'middle',
              userSelect: 'none',
            }}
          />
        )}
      </div>
    </Footer>
  );
});

AppFooter.displayName = 'AppFooter';
